import {
  IHsHeading,
  IHsSubheading,
  IInventoryUnit,
  IProduct,
  IProductCategory,
  IProductPricesDto,
} from '../../features/Products/types'
import { apiSlice } from './apiQuery'

export const apiProducts = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    createHsLocalTariff: build.mutation<any, { tenantId: number; customerId?: number; body: any }>({
      query: ({ tenantId, body }) => ({
        url: `/tenant/${tenantId}/product/category/tariffs/local`,
        method: 'POST',
        domain: 'Product',
        body,
      }),
      invalidatesTags: (_result, _error, { customerId, body }) => {
        const categoryId = body.productCategoryId
        const tags: Array<{ type: 'ProductCategories'; id: number | string }> = []
        if (customerId) {
          tags.push({ type: 'ProductCategories', id: customerId })
          if (categoryId) {
            tags.push({ type: 'ProductCategories', id: `${customerId}-${categoryId}` })
          }
        }
        return tags
      },
    }),

    createProduct: build.mutation<any, { tenantId?: number; customerId?: number; body: IProduct }>({
      query: ({ tenantId, customerId, body }) => ({
        url: customerId ? `/customer/${customerId}/product` : `/tenant/${tenantId}/product`,
        method: 'POST',
        domain: 'Product',
        body,
      }),
      invalidatesTags: (_result, _error, { customerId }) => [{ type: 'Products', id: customerId }],
    }),

    createProductCategory: build.mutation<
      any,
      { tenantId?: number; customerId?: number; body: IProductCategory }
    >({
      query: ({ tenantId, customerId, body }) => ({
        url: customerId
          ? `/customer/${customerId}/product/category`
          : `/tenant/${tenantId}/product/category`,
        method: 'POST',
        domain: 'Product',
        body,
      }),
      invalidatesTags: (_result, _error, { customerId }) => [
        { type: 'ProductCategories', id: customerId },
      ],
    }),

    getAllHsHeadings: build.query<IHsHeading[], { tenantId: number }>({
      query: () => ({
        url: '/hsheading',
        method: 'GET',
        domain: 'Product',
      }),
      providesTags: () => [{ type: 'HSHeadings' }],
    }),

    getAllSubHsHeadings: build.query<IHsSubheading[], { tenantId: number }>({
      query: () => ({
        url: '/subheading',
        method: 'GET',
        domain: 'Product',
      }),
      providesTags: () => [{ type: 'SubHSHeadings' }],
    }),

    getAllInventoryUnits: build.query<IInventoryUnit[], void>({
      query: () => ({
        url: '/InventoryUnit',
        method: 'GET',
        domain: 'Product',
      }),
      providesTags: () => [{ type: 'InventoryUnits' }],
    }),

    getInventoryUnitById: build.query<IInventoryUnit, { id: number }>({
      query: ({ id }) => ({
        url: `/inventory/units/${id}`,
        method: 'GET',
        domain: 'Product',
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'InventoryUnits', id }],
    }),

    getProductCategories: build.query<
      IProductCategory[],
      { tenantId?: number; customerId?: number }
    >({
      query: ({ tenantId, customerId }) => ({
        url: customerId
          ? `/customer/${customerId}/product/category`
          : `/tenant/${tenantId}/product/category`,
        method: 'GET',
        domain: 'Product',
      }),
      transformResponse: (response: IProductCategory[]) => {
        return response.map((category) => ({
          ...category,
          inUse: category.inUse ?? false,
        }))
      },
      providesTags: (_result, _error, { customerId }) => [
        { type: 'ProductCategories', id: customerId },
      ],
    }),

    getProductCategory: build.query<
      IProductCategory,
      { tenantId?: number; customerId?: number; categoryId: number }
    >({
      query: ({ tenantId, customerId, categoryId }) => ({
        url: customerId
          ? `/customer/${customerId}/product/category/${categoryId}`
          : `/tenant/${tenantId}/product/category/${categoryId}`,
        method: 'GET',
        domain: 'Product',
      }),
      providesTags: (_result, _error, { customerId, categoryId }) => [
        { type: 'ProductCategories', id: `${customerId}-${categoryId}` },
      ],
    }),

    getProduct: build.query<
      IProduct,
      { tenantId?: number; customerId?: number; productId: number }
    >({
      query: ({ tenantId, customerId, productId }) => ({
        url: customerId
          ? `/customer/${customerId}/product/${productId}`
          : `/tenant/${tenantId}/product/${productId}`,
        method: 'GET',
        domain: 'Product',
      }),
      providesTags: (_result, _error, { customerId, productId }) => [
        { type: 'Products', id: `${customerId}-${productId}` },
      ],
    }),

    getProducts: build.query<IProduct[], { tenantId?: number; customerId?: number }>({
      query: ({ tenantId, customerId }) => ({
        url: customerId ? `/customer/${customerId}/product` : `/tenant/${tenantId}/product`,
        method: 'GET',
        domain: 'Product',
      }),
      transformResponse: (response: IProduct[]) => {
        return response.map((product) => ({
          ...product,
          inUse: product.inUse ?? false,
        }))
      },
      providesTags: (_result, _error, { customerId }) => [{ type: 'Products', id: customerId }],
    }),

    updateProductCategory: build.mutation<
      any,
      { tenantId?: number; customerId?: number; productId: number; body: IProductCategory }
    >({
      query: ({ tenantId, customerId, productId, body }) => ({
        url: customerId
          ? `/customer/${customerId}/product/${productId}/category`
          : `/tenant/${tenantId}/product/${productId}/category`,
        method: 'PUT',
        domain: 'Product',
        body,
      }),
      invalidatesTags: (_result, _error, { customerId, productId, body }) => {
        return [
          { type: 'Products', id: `${customerId}-${productId}` },
          { type: 'Products', id: customerId },
          { type: 'ProductCategories', id: customerId },
          { type: 'ProductCategories', id: `${customerId}-${body.id}` },
        ]
      },
    }),

    updateProduct: build.mutation<any, { tenantId?: number; customerId?: number; body: IProduct }>({
      query: ({ tenantId, customerId, body }) => ({
        url: customerId ? `/customer/${customerId}/product` : `/tenant/${tenantId}/product`,
        method: 'PUT',
        domain: 'Product',
        body,
      }),
      invalidatesTags: (_result, _error, { customerId, body }) => [
        { type: 'Products', id: `${customerId}-${body.id}` },
        { type: 'Products', id: customerId },
      ],
    }),

    deleteProduct: build.mutation<
      any,
      { tenantId?: number; customerId?: number; productId: number }
    >({
      query: ({ tenantId, customerId, productId }) => ({
        url: customerId
          ? `/customer/${customerId}/product/${productId}`
          : `/tenant/${tenantId}/product/${productId}`,
        method: 'DELETE',
        domain: 'Product',
      }),
      invalidatesTags: (_result, _error, { customerId, productId }) => [
        { type: 'Products', id: `${customerId}-${productId}` },
        { type: 'Products', id: customerId },
      ],
    }),

    getProductDates: build.query<
      string[],
      { tenantId?: number; customerId?: number; productId: number; currencyId: number }
    >({
      query: ({ tenantId, customerId, productId, currencyId }) => ({
        url: customerId
          ? `/customer/${customerId}/product/${productId}/currency/${currencyId}`
          : `/tenant/${tenantId}/product/${productId}/currency/${currencyId}`,
        method: 'GET',
        domain: 'Product',
      }),
      providesTags: (_result, _error, { customerId, productId }) => [
        { type: 'Products', id: `${customerId}-${productId}` },
      ],
    }),

    getProductPrices: build.query<
      IProductPricesDto,
      {
        tenantId?: number
        customerId?: number
        productId: number
        currencyId: number
        updatedAt: string
      }
    >({
      query: ({ tenantId, customerId, productId, currencyId, updatedAt }) => ({
        url: customerId
          ? `/customer/${customerId}/product/${productId}/currency/${currencyId}/updatedAt/${updatedAt}`
          : `/tenant/${tenantId}/product/${productId}/currency/${currencyId}/updatedAt/${updatedAt}`,
        method: 'GET',
        domain: 'Product',
      }),
      providesTags: (_result, _error, { customerId, productId }) => [
        { type: 'Products', id: `${customerId}-${productId}` },
      ],
    }),

    getCustomerBrands: build.query<any[], { customerId: number }>({
      query: ({ customerId }) => ({
        url: `/customer/${customerId}/brand`,
        method: 'GET',
        domain: 'Product',
      }),
      providesTags: (result, error, { customerId }) => [{ type: 'Brands', id: customerId }],
    }),

    getCustomerBrandById: build.query<any, { customerId: number; id: number }>({
      query: ({ customerId, id }) => ({
        url: `/customer/${customerId}/brand/${id}`,
        method: 'GET',
        domain: 'Product',
      }),
      providesTags: (result, error, { customerId, id }) => [{ type: 'Brands', id }],
    }),

    createCustomerBrand: build.mutation<any, { customerId: number; body: any }>({
      query: ({ customerId, body }) => ({
        url: `/customer/${customerId}/brand`,
        method: 'POST',
        domain: 'Product',
        body,
      }),
      invalidatesTags: (result, error, { customerId }) => [{ type: 'Brands', id: customerId }],
    }),

    updateCustomerBrand: build.mutation<any, { customerId: number; body: any }>({
      query: ({ customerId, body }) => ({
        url: `/customer/${customerId}/brand`,
        method: 'PUT',
        domain: 'Product',
        body,
      }),
      invalidatesTags: (result, error, { customerId }) => [{ type: 'Brands', id: customerId }],
    }),

    deleteProductCategory: build.mutation<
      any,
      { tenantId?: number; customerId?: number; categoryId: number }
    >({
      query: ({ tenantId, customerId, categoryId }) => ({
        url: customerId
          ? `/customer/${customerId}/product/category/${categoryId}`
          : `/tenant/${tenantId}/product/category/${categoryId}`,
        method: 'DELETE',
        domain: 'Product',
      }),
      invalidatesTags: (_result, _error, { customerId }) => [
        { type: 'ProductCategories', id: customerId },
      ],
    }),

    deleteCustomerBrand: build.mutation<any, { customerId: number; brandId: number }>({
      query: ({ customerId, brandId }) => ({
        url: `/customer/${customerId}/brand/${brandId}`,
        method: 'DELETE',
        domain: 'Product',
      }),
      invalidatesTags: (result, error, { customerId }) => [{ type: 'Brands', id: customerId }],
    }),
  }),
  overrideExisting: false,
})

export const {
  // Mutations
  useCreateHsLocalTariffMutation,
  useCreateProductMutation,
  useCreateProductCategoryMutation,
  useUpdateProductCategoryMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useDeleteProductCategoryMutation,
  // Brand Mutations
  useCreateCustomerBrandMutation,
  useUpdateCustomerBrandMutation,
  useDeleteCustomerBrandMutation,
  // Queries
  useGetAllHsHeadingsQuery,
  useGetAllSubHsHeadingsQuery,
  useGetAllInventoryUnitsQuery,
  useGetInventoryUnitByIdQuery,
  useGetProductCategoriesQuery,
  useLazyGetProductCategoriesQuery,
  useGetProductCategoryQuery,
  useGetProductQuery,
  useGetProductsQuery,
  useGetProductDatesQuery,
  useGetProductPricesQuery,
  useLazyGetProductPricesQuery,
  useLazyGetProductDatesQuery,
  useLazyGetProductQuery,
  // Brand Queries
  useGetCustomerBrandsQuery,
  useGetCustomerBrandByIdQuery,
} = apiProducts
